import { getFormErrorMessage } from "@core/utility/get-error-message";
import { QuoteAccommodationFormRule, QuoteAccommodationFormSchema } from "@factory/form/form.rule";
import { useFormDefaultQuoteAccommodation } from "@factory/form/form-default-quote-accommodation.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseQuoteAccommodationModel } from "@factory/model/query-model-quote-accommodation";
import { 
  useQyDeleteQuoteAccommodation, 
  useQyGetQuoteAccommodationById, 
  useQyUpdateQuoteAccommodation} from "@factory/query/quote-accommodation.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditQuoteAccommodation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultQuoteAccommodation();
  const navigate = useNavigate();
  const { mapUpdateQuoteAccommodation, mapDeleteQuoteAccommodation } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { quoteAccommodationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID QuoteAccommodation
  const handleGetApiSuccess = (data: GetResponseQuoteAccommodationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("quotation", responseData?.quotation || "");
      setValue("hotel", responseData?.hotel || "");
      setValue("roomType", responseData?.room_type || "");
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
      setValue("validFrom", responseData?.valid_from || "");
      setValue("validUntil", responseData?.valid_until || "");
      setValue("name", responseData?.name || "");
      setValue("price", responseData?.price);
  };
  const {
    data: quoteAccommodationResponse,
    isLoading,
    isError: quoteAccommodationError,
  } = useQyGetQuoteAccommodationById(quoteAccommodationId || "", handleGetApiSuccess);
  const quoteAccommodationData = quoteAccommodationResponse?.data?.[0];

  // API UPDATE QuoteAccommodation
  const handleUpdateApiSuccess = () => {
    showSuccess("QuoteAccommodation updated successfully");
  };
  const { mutate: updateQuoteAccommodation, isLoading: isEditLoading } = useQyUpdateQuoteAccommodation(
    handleUpdateApiSuccess
  );

  // API DELETE QuoteAccommodation
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("QuoteAccommodation deleted successfully");
  };
  const { mutate: deleteQuoteAccommodation } = useQyDeleteQuoteAccommodation(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<QuoteAccommodationFormSchema>({
    defaultValues: getDefault(quoteAccommodationData),
    resolver: zodResolver(QuoteAccommodationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: QuoteAccommodationFormSchema) => {
    if (!quoteAccommodationData || !quoteAccommodationData.code) {
      throw new Error("No quoteAccommodation data");
    }

    const formData = mapUpdateQuoteAccommodation(form, quoteAccommodationData.code);
    updateQuoteAccommodation(formData);
  };
  const handleValidateError = (err: FieldErrors<QuoteAccommodationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!quoteAccommodationData || !quoteAccommodationData.code) {
      throw new Error("No quoteAccommodation data found");
    }

    const form = mapDeleteQuoteAccommodation(quoteAccommodationData.code);
    deleteQuoteAccommodation(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    quoteAccommodationId,
    quoteAccommodationData,
    isLoading,
    quoteAccommodationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}